import { useEffect, useRef, useContext, useCallback } from 'react';
import {
  UNSAFE_NavigationContext as NavigationContext,
  useNavigate,
} from 'react-router-dom';
import UnsavedChangesContext from '../../../contexts/UnsavedChangesContext';

/**
 * useUnsavedChangesWarning - Blocks in-app navigation, browser back button,
 * and browser reload when there are unsaved form changes.
 *
 * Three layers of protection:
 * 1. navigator.push override — catches sidebar clicks and navigate(path) calls
 * 2. popstate guard entry — catches browser back/forward buttons
 * 3. beforeunload listener — catches browser refresh / tab close
 *
 * The modal offers two choices:
 * - "Discard"      — navigates away, losing changes
 * - "Keep in Page" — closes modal, stays on current page
 *
 * The modal is rendered once by UnsavedChangesProvider — forms never need to
 * render <UnsavedChangesModal> themselves.
 *
 * @param {boolean} isDirty - Whether the form has unsaved changes
 * @returns {{ navigateBack: Function, allowNavigation: Function }}
 */
const useUnsavedChangesWarning = (isDirty = false) => {
  const pendingNavigationRef = useRef(null);
  const isNavigatingRef = useRef(false);

  const { navigator } = useContext(NavigationContext);
  const navigate = useNavigate();
  const { showModal, hideModal } = useContext(UnsavedChangesContext);

  const confirmNavigation = useCallback(() => {
    hideModal();
    isNavigatingRef.current = true;

    if (pendingNavigationRef.current) {
      const { type, args } = pendingNavigationRef.current;
      pendingNavigationRef.current = null;

      if (type === 'push') {
        if (window.history.state?.guard) {
          window.history.replaceState(null, '', window.location.href);
        }
        navigate(args[0]);
      } else if (type === 'popstate') {
        window.history.go(-2);
      }

      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 3000);
    } else {
      isNavigatingRef.current = false;
    }
  }, [navigate, hideModal]);

  const cancelNavigation = useCallback(() => {
    hideModal();
    pendingNavigationRef.current = null;
  }, [hideModal]);

  // Refs keep openModal stable: showModal is recreated rarely; confirm/cancel
  // must always invoke the latest implementations.
  const confirmRef = useRef(confirmNavigation);
  confirmRef.current = confirmNavigation;
  const cancelRef = useRef(cancelNavigation);
  cancelRef.current = cancelNavigation;

  const openModal = useCallback(() => {
    showModal({
      onConfirm: () => confirmRef.current?.(),
      onCancel: () => cancelRef.current?.(),
    });
  }, [showModal]);

  // Layer 1: Override navigator.push for in-app link clicks (sidebar, etc.)
  useEffect(() => {
    if (!navigator) return;
    const push = navigator.push;
    navigator.push = (...args) => {
      if (!isDirty || isNavigatingRef.current) {
        push.apply(navigator, args);
      } else {
        pendingNavigationRef.current = { type: 'push', args };
        openModal();
      }
    };
    return () => {
      navigator.push = push;
    };
  }, [isDirty, navigator, openModal]);

  // Layer 2: Guard against browser back/forward buttons via popstate.
  useEffect(() => {
    if (!isDirty) return;

    window.history.pushState({ guard: true }, '', window.location.href);

    const handlePopState = () => {
      if (isNavigatingRef.current) return;
      window.history.pushState({ guard: true }, '', window.location.href);
      pendingNavigationRef.current = { type: 'popstate' };
      openModal();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (window.history.state?.guard && !isNavigatingRef.current) {
        isNavigatingRef.current = true;
        window.history.back();
        setTimeout(() => {
          isNavigatingRef.current = false;
        }, 100);
      }
    };
  }, [isDirty, openModal]);

  // Layer 3: Browser reload / tab close
  // Use a ref to track navigating state for the beforeunload handler
  const isNavigatingForUnloadRef = useRef(false);

  useEffect(() => {
    if (!isDirty) return;

    const handler = (e) => {
      // Skip warning if navigation is explicitly allowed (e.g., payment redirect)
      if (isNavigatingRef.current || isNavigatingForUnloadRef.current) {
        return;
      }
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Sync ref with isNavigatingRef for beforeunload handler access
  useEffect(() => {
    isNavigatingForUnloadRef.current = isNavigatingRef.current;
  });

  const allowNavigation = useCallback(() => {
    isNavigatingRef.current = true;
    isNavigatingForUnloadRef.current = true;
    setTimeout(() => {
      isNavigatingRef.current = false;
      isNavigatingForUnloadRef.current = false;
    }, 5000);
  }, []);

  const navigateBack = useCallback(() => {
    if (isNavigatingRef.current) return;
    isNavigatingRef.current = true;
    const delta = window.history.state?.guard ? -2 : -1;
    navigator.go(delta);
    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 3000);
  }, [navigator]);

  return {
    navigateBack,
    allowNavigation,
  };
};

export default useUnsavedChangesWarning;
